/* eslint-disable no-useless-catch */
import * as Sentry from '@sentry/vue'
import PresentationApi from '@/api/presentation'
import Socket from '@/socket'
import router from '@/router'
import Utils from '@/util'
import Constant from '@/constant'
import { EVENT_CATEGORIES_BY_ROUTE_NAME } from '@/constant/routes'
import store from '@/vuex/store'
import configs from '@/config'
import { fetchCanvasBlocksForSlides } from '@/utils/CanvasBlocksLoader.utils'

const setPresentationAction = ({ commit }, presentation) => {
  commit('setPresentation', presentation)
}

const createNewPresentationAction = async (
  {
    state, dispatch, commit, rootGetters,
  },
  {
    trigger, onSuccess, onFailure, queryParams = {},
  },
) => {
  const { selectedFolderId } = state.selections
  const {
    currentUser, currentAccount, workingMode, presentations,
  } = state
  commit('setIsCreatingNewPresentation', true)
  const language = (currentUser.rememberSettings && currentUser.rememberSettings.language) ?
    currentUser.rememberSettings.language : null
  try {
    const defaultSlideConfig = rootGetters['slideModule/defaultSlideConfig']
    const payload = {
      name: Constant.presentation.defaultPresentationName,
      accessCode: '',
      socketId: Socket.getSocketId(),
      folderId: selectedFolderId,
      hasDefaultSlide: true,
      fromWorkspace: workingMode === 'team' && trigger !== Constant.Events.TRIGGER.BLANK_FROM_ONBOARDING_V3,
      lang: language,
      defaultSlideConfig,
    }
    if (currentUser.personalInformation && currentUser.personalInformation.presenterLanguage) {
      payload.language = currentUser.personalInformation.presenterLanguage
    }

    const newPresentation = await PresentationApi.createNewPresentation(payload)

    newPresentation.User = currentUser
    if (trigger === 'Quiz hints Banner') {
      state.createdPresentationFromQuizHintsBanner = true
    }
    const query = {
      new: true,
      ...queryParams,
    }
    if (state.isPowerPointAddIn) {
      delete query.new
    }
    if (store.state.isEnableAddinPowerpoint) {
      window.location.href = `${configs.presenterDomain}/presentation/${newPresentation.id}`
      return
    }
    router.push({
      name: 'PresentationDetail',
      params: { id: newPresentation.id },
      query,
    })
    const teamId = workingMode === 'team' ? currentAccount.id : null
    const isTeamPresentation = workingMode === 'team'
    // In the most case of our tracking data system, eventCategory is the name of the current route
    let eventCategory = ''
    if (router.currentRoute && router.currentRoute.name) {
      eventCategory = EVENT_CATEGORIES_BY_ROUTE_NAME[router.currentRoute.name]
    }
    Utils.mixpanelTracking(
      eventCategory,
      'Create new presentation',
      trigger,
      null,
      {
        presentationId: newPresentation.id,
        teamId,
        isTeamPresentation,
      },
    )
    // doạn track này gây lỗi cần xem lại vấn đề lỗi là gì
    // if (getters.abTestGroup === 2 || getters.abTestGroup === 3) {
    //   const slideTracker = new SlideMixpanelTracker(this.presentation, this.$route.name)
    //   slideTracker.setCustomProps({
    //     isDefaultSlide: true,
    //   })
    //   slideTracker.trackCreateNewSlide(null, null)
    // }
    dispatch('setPresentationsAction', [...presentations, newPresentation])
    if (onSuccess) {
      onSuccess(newPresentation)
    }
  } catch (e) {
    Sentry.captureException(e)
    commit('setIsCreatingNewPresentation', false)
    if (onFailure) {
      onFailure(e)
    }
  }
}

const setMorePresentationsLoadingAction = ({ commit }, payload) => {
  commit('setMorePresentationsLoading', payload)
}

const patchPresentationAction = ({ commit, state, dispatch }, partialPresentation) => {
  commit('setPresentation', { ...state.presentation, ...partialPresentation })

  // Whenever update reportUrl, dispatch the action to get the report data
  if (partialPresentation.exportData && partialPresentation.exportData.reportUrl) {
    dispatch('presentationReport/getPresentationReport', partialPresentation)
  }
}

const onlyPatchPresentationAction = ({ commit, state }, partialPresentation) => {
  commit('setPresentation', { ...state.presentation, ...partialPresentation })
}

const toggleQRCodePanelAction = ({ commit }, isOpen) => {
  commit('setQRCodeVisible', isOpen)
}

const openAudiencePrivateFeedbackAction = async ({ commit }, presentation) => {
  commit('setIsOpeningPrivateFeedback', true)
  await PresentationApi.openPrivateFeedback(presentation)
  commit('setIsOpeningPrivateFeedback', false)
}

const setPresentFromBeginningAction = async ({ commit, dispatch }, presentation) => {
  const presentationDetail = await PresentationApi.getPresentationDetails(presentation.id)
  const slides = presentationDetail.body.Slides
  slides.sort((a, b) => a.order - b.order)
  const firstSlide = slides[0]
  dispatch('setSlideActiveAction', firstSlide)

  await PresentationApi.updatePresentationByPresentationId(presentation.id, {
    activeSlide: firstSlide.id,
  })

  commit('setPresentFromBeginning', true)
}

const updatePresentationAction = async ({ commit }, payload) => {
  commit('updatePresentation', payload)
}

const getPresentationByIdAction = async ({ dispatch }, { presentationId, payloadDataPresentation }) => {
  let res = null
  try {
    await dispatch('featureToggles/handlePresentationLoadFlagUnleash', presentationId)
    res = await PresentationApi.getPresentationDetails(presentationId, payloadDataPresentation)
    const slideIds = res.body.Slides.map((slide) => slide.id)
    dispatch('subscribeSlidesAction', { slideIds, presentationId })
    await dispatch('presentationAttributeModule/getPresentationAttributeAction', presentationId)
    // We can move it upper to avoid reload Themes everytime when we open a new presentation
    // But I prefer to keep it here to get update from offical Theme more frequently,
    // but not frequently as put it in the ThemeSetting.vue (call everytime when open the Design Tabs)
    dispatch('themeModule/getThemesAction')
    dispatch('themeModule/getOfficialThemesAction')
    dispatch('presetModule/getPresetColorPalettesAction')
    dispatch('themeModule/getTeamThemesAction')
    return res
  // eslint-disable-next-line sonarjs/no-useless-catch
  } catch (err) {
    throw err
  }
}

const updatePresentationByIdAction = async ({ commit }, { id, payload }) => {
  commit('updatePresentation', payload)
  PresentationApi.updatePresentationByPresentationId(id, payload)
}

const subscribeSlidesAction = async ({ dispatch }, payload) => {
  if (!payload.presentationId || !payload.slideIds || configs.region === Constant.euRegion) return
  dispatch('kvSync/subscribeKeyAction', {
    key: `/presentations/${payload.presentationId}/slidesList`,
  })
  payload.slideIds.forEach((slideId) => {
    dispatch('kvSync/subscribeKeyAction', {
      key: `/slides/${slideId}/#`,
    })
  })
}

const addSlidesAction = async ({ commit, rootState }, payload) => {
  const { slides, insertAfter: insertAfterParams } = payload

  if (!slides || !slides.length) return

  // If insertAfter is undefined, default to the last slide so new slides are appended at presentation's end.
  let insertAfter
  if (!insertAfterParams && rootState.presentation?.slides?.length) {
    insertAfter = rootState.presentation?.slides?.length - 1
  }

  const slidesWithFetchedCanvasBlocks = await fetchCanvasBlocksForSlides(slides)

  commit('addSlides', {
    slidesToAdd: slidesWithFetchedCanvasBlocks,
    insertAfter,
  })
}

export default {
  setPresentationAction,
  setPresentation: setPresentationAction,
  createNewPresentationAction,
  patchPresentationAction,
  updatePresentation: patchPresentationAction,
  onlyPatchPresentationAction,
  setMorePresentationsLoadingAction,
  openAudiencePrivateFeedbackAction,
  toggleQRCodePanelAction,
  setPresentFromBeginningAction,
  updatePresentationAction,
  getPresentationByIdAction,
  updatePresentationByIdAction,
  subscribeSlidesAction,
  addSlidesAction,
}
